import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Movie from "../components/Movie";
import MovieSkeleton from "../components/MovieSkeleton";
import { getFavorites, toggleFavorite } from "../utils/favorites";

const MOVIES_BATCH_SIZE = 5;

function Home() {
  // UI 상태: 로딩/데이터/오류
  const [loading, setLoading] = useState(true);
  const [movies, setMovies] = useState([]);
  const [genres, setGenres] = useState([]);
  const [error, setError] = useState("");
  // 검색/필터/정렬 상태
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [genre, setGenre] = useState("");
  const [minRating, setMinRating] = useState("0");
  const [year, setYear] = useState("");
  const [sort, setSort] = useState("rating");
  // 페이지네이션 및 수동 재시도 트리거
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  // localStorage 즐겨찾기 id 목록(빠른 포함 여부 체크용)
  const [favoriteIds, setFavoriteIds] = useState([]);

  // localStorage에서 즐겨찾기를 읽어 현재 화면 상태와 동기화한다.
  const loadFavoriteIds = () => {
    setFavoriteIds(getFavorites().map((movie) => movie.id));
  };

  useEffect(() => {
    // 앱 내 다른 화면에서 즐겨찾기를 변경하면 커스텀 이벤트로 동기화한다.
    loadFavoriteIds();
    const onFavoritesUpdated = () => loadFavoriteIds();
    window.addEventListener("favorites-updated", onFavoritesUpdated);
    return () => window.removeEventListener("favorites-updated", onFavoritesUpdated);
  }, []);

  useEffect(() => {
    // 필터 UI에 표시할 장르 목록을 최초 1회 로드한다.
    const fetchGenres = async () => {
      try {
        const response = await fetch("/api/genres");
        if (!response.ok) return;
        const data = await response.json();
        setGenres(data.data.genres || []);
      } catch {
        setGenres([]);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    // 즉시 요청이 과도하게 발생하지 않도록 검색어 입력을 400ms 지연시킨다.
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    // 검색/필터/정렬 조건이 바뀌면 항상 1페이지부터 다시 조회한다.
    setPage(1);
  }, [debouncedQuery, genre, minRating, year, sort]);

  useEffect(() => {
    // 조건 변화(검색,필터,정렬,페이지,재시도)마다 영화 목록을 다시 조회한다.
    // AbortController로 이전 요청을 취소해 응답 역전(race condition)을 방지한다.
    const controller = new AbortController();
    const fetchMovies = async () => {
      try {
        setLoading(true);
        // 백엔드 API가 기대하는 쿼리 파라미터를 조립한다.
        const params = new URLSearchParams({
          page: String(page),
          sort,
          minRating: String(minRating),
          batch: String(MOVIES_BATCH_SIZE),
        });
        if (debouncedQuery) params.set("query", debouncedQuery);
        if (genre) params.set("genre", genre);
        if (year) params.set("year", year);

        const response = await fetch(`/api/movies?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // API 응답 포맷(data.data.*)을 화면 상태로 반영한다.
        setMovies(data.data.movies || []);
        setTotalPages(Math.max(1, data.data.total_pages || 1));
        setError("");
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching movies:", err);
        setError(err.message || "Failed to fetch movies.");
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
    return () => controller.abort();
  }, [debouncedQuery, genre, minRating, page, refreshKey, sort, year]);

  // 목록 카드의 별 버튼 클릭 시 localStorage 즐겨찾기를 토글한다.
  const onToggleFavorite = (movieId) => {
    const target = movies.find((movie) => movie.id === movieId);
    if (!target) return;
    toggleFavorite({
      id: target.id,
      title: target.title,
      summary: target.summary,
      genres: target.genres,
      rating: target.rating,
      year: target.year,
      medium_cover_image: target.medium_cover_image,
      large_cover_image: target.large_cover_image,
    });
    loadFavoriteIds();
  };

  // 동일 조건으로 다시 API 요청하기 위한 카운터 증가
  const onRetry = () => setRefreshKey((prev) => prev + 1);
  const isSearching = debouncedQuery.length > 0;
  // 현재 연도부터 최근 40년을 연도 필터 옵션으로 제공
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 40 }, (_, index) => current - index);
  }, []);

  return (
    <div className="page">
      <header className="topbar">
        <h1>Movie Explorer</h1>
        <Link className="link-btn" to="/favorites">
          Favorites ({favoriteIds.length})
        </Link>
      </header>

      <section className="control-panel">
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search movie title..."
        />
        <select value={genre} onChange={(event) => setGenre(event.target.value)}>
          <option value="">All genres</option>
          {genres.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select value={minRating} onChange={(event) => setMinRating(event.target.value)}>
          <option value="0">All ratings</option>
          <option value="5">5+ rating</option>
          <option value="6">6+ rating</option>
          <option value="7">7+ rating</option>
          <option value="8">8+ rating</option>
        </select>
        <select value={year} onChange={(event) => setYear(event.target.value)}>
          <option value="">All years</option>
          {yearOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="rating">Sort by rating</option>
          <option value="latest">Sort by latest</option>
        </select>
      </section>

      {/* 렌더 분기: 로딩 -> 오류 -> 빈 결과 -> 정상 목록 순서 */}
      {loading ? (
        <section className="movie-grid">
          {Array.from({ length: 8 }, (_, index) => (
            <MovieSkeleton key={index} />
          ))}
        </section>
      ) : error ? (
        <section className="state-box">
          <h2>Failed to load movies</h2>
          <p>{error}</p>
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        </section>
      ) : movies.length === 0 ? (
        <section className="state-box">
          <h2>{isSearching ? "No movies found." : "No movies available."}</h2>
        </section>
      ) : (
        <>
          <section className="movie-grid">
            {movies.map((movie) => (
              <Movie
                key={movie.id}
                id={movie.id}
                coverImg={movie.medium_cover_image}
                title={movie.title}
                summary={movie.summary}
                genres={movie.genres}
                rating={movie.rating}
                year={movie.year}
                isFavorite={favoriteIds.includes(movie.id)}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </section>
          <footer className="pagination">
            <button type="button" onClick={() => setPage((prev) => prev - 1)} disabled={page <= 1}>
              Previous
            </button>
            <span>
              Page {page} / {Math.min(totalPages, 500)}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page >= Math.min(totalPages, 500)}
            >
              Next
            </button>
          </footer>
        </>
      )}
    </div>
  );
}

export default Home;
